import React, { useState } from 'react';
import { Card, Tabs, Tag, Button, Typography, Progress, message, Segmented, Table } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FileZipOutlined, SyncOutlined, CheckCircleOutlined, BarChartOutlined, TableOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { ChartData, FlowFileResult } from '../types';
import { smartReviewService } from '../services/smartReviewService';
import { FileDownloader } from '../utils/fileDownloader';
import { createChartOptions } from '../utils/chartOptions';

const { Text } = Typography;

const ChartDataTable: React.FC<{ chart: ChartData }> = ({ chart }) => {
  const columns = [
    {
      title: '',
      dataIndex: 'seriesName',
      key: 'seriesName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    ...chart.categories.map((category, index) => ({
      title: category,
      dataIndex: `value_${index}`,
      key: `value_${index}`,
      align: 'right' as const,
    })),
  ];

  const dataSource = chart.series.map((series, index) => ({
    key: index.toString(),
    seriesName: series.name,
    ...Object.fromEntries(series.data.map((value, i) => [`value_${i}`, value])),
  }));

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      size="small"
      bordered
    />
  );
};

const FileResultPane: React.FC<{ file: FlowFileResult }> = ({ file }) => {
  const [downloading, setDownloading] = useState(false);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await smartReviewService.download(file.file_id);
      FileDownloader.saveBlob(blob, file.filename);
      message.success(`Downloaded ${file.filename}`);
    } catch (error) {
      console.error('Error downloading file:', error);
      message.error(`Failed to download ${file.filename}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
        <Text type="secondary" ellipsis={{ tooltip: file.filename }}>
          {file.zipped ? (
            <FileZipOutlined style={{ color: '#faad14', marginRight: 8 }} />
          ) : (
            <FileExcelOutlined style={{ color: '#217346', marginRight: 8 }} />
          )}
          {file.filename}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {file.chart && (
            <Segmented
              value={viewMode}
              onChange={(value) => setViewMode(value as 'chart' | 'table')}
              options={[
                { value: 'chart', icon: <BarChartOutlined /> },
                { value: 'table', icon: <TableOutlined /> },
              ]}
            />
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={downloading}
          >
            {file.zipped ? 'Download ZIP (all files)' : 'Download'}
          </Button>
        </div>
      </div>
      {file.chart && (
        viewMode === 'chart' ? (
          <Chart
            options={createChartOptions(file.chart)}
            series={file.chart.series}
            type="bar"
            height={350}
          />
        ) : (
          <ChartDataTable chart={file.chart} />
        )
      )}
    </div>
  );
};

interface FileGroupTabsProps {
  title: string;
  files: FlowFileResult[];
  expectedTotal?: number;
  /** Entity of this category currently being processed (polling page only) */
  processingEntity?: string | null;
}

export const FileGroupTabs: React.FC<FileGroupTabsProps> = ({ title, files, expectedTotal, processingEntity }) => {
  const [downloadingAll, setDownloadingAll] = useState(false);

  if (files.length === 0) {
    return null;
  }

  const showProgress = expectedTotal !== undefined && expectedTotal > 0;
  const isComplete = showProgress && files.length >= expectedTotal;

  const uniqueFiles = Array.from(new Map(files.map((file) => [file.file_id, file])).values());

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    let downloaded = 0;
    try {
      for (const file of uniqueFiles) {
        const blob = await smartReviewService.download(file.file_id);
        FileDownloader.saveBlob(blob, file.filename);
        downloaded += 1;
      }
      message.success(`Downloaded ${downloaded} file${downloaded === 1 ? '' : 's'}`);
    } catch (error) {
      console.error('Error downloading files:', error);
      message.error(`Download failed after ${downloaded} of ${uniqueFiles.length} files`);
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Tag color="orange" style={{ margin: 0 }}>
            {files.length} Excels
          </Tag>
          <span>{title}</span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {showProgress && (
            <>
              <Tag
                color={isComplete ? 'success' : 'processing'}
                icon={isComplete ? <CheckCircleOutlined /> : <SyncOutlined spin />}
                style={{ margin: 0 }}
              >
                {isComplete
                  ? 'Completed'
                  : `Processing${processingEntity ? ` ${processingEntity}` : ''} — ${files.length}/${expectedTotal}`}
              </Tag>
              <Progress
                type="circle"
                size={32}
                percent={Math.round((files.length / expectedTotal) * 100)}
                status={isComplete ? 'success' : 'active'}
              />
            </>
          )}
          {uniqueFiles.length > 1 && (
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadAll}
              loading={downloadingAll}
            >
              Download all
            </Button>
          )}
        </div>
      }
      style={{ marginBottom: 20 }}
    >
      <Tabs
        defaultActiveKey={`${files[0].file_id}-${files[0].position}`}
        items={files.map((file) => ({
          key: `${file.file_id}-${file.position}`,
          label: `${file.entity_name || file.filename} Analysis`,
          children: <FileResultPane file={file} />,
        }))}
      />
    </Card>
  );
};
